import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Search, Star, MapPin, X } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { useLang } from "@/i18n/LanguageContext";
import { hotels, zonePricing, hotelLabels, roundTripMultiplier } from "./hotelsData";

const HERO_IMG =
  "https://images.unsplash.com/photo-1519046904884-53103b34b206?w=2000&q=80";

const StarBadge = ({ n }) => (
  <div className="flex items-center gap-0.5">
    {Array.from({ length: n }).map((_, idx) => (
      <Star key={idx} className="w-3 h-3 fill-[#C6A87C] text-[#C6A87C]" />
    ))}
  </div>
);

const HotelRow = ({ hotel, zone, labels, onSelect }) => (
  <button
    data-testid={`hotel-row-${hotel.id}`}
    onClick={() => onSelect(hotel)}
    className="w-full text-left bg-white border border-[#EAE6DF] p-5 sm:p-6 hover:border-[#1A1F24] hover:shadow-lg transition-all group"
  >
    <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 sm:items-center">
      <div className="sm:col-span-6">
        <div className="flex items-center gap-3 mb-1">
          <h3 className="font-serif text-xl text-[#1A1F24] leading-tight">{hotel.name}</h3>
        </div>
        <div className="flex items-center gap-4 text-xs text-[#4A5568]">
          <span className="flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {zone.label[labels.__lang]}
          </span>
          <StarBadge n={hotel.stars} />
        </div>
      </div>
      <div className="sm:col-span-4 flex sm:flex-col gap-4 sm:gap-1 items-baseline sm:items-end">
        <p className="text-xs uppercase tracking-[0.2em] text-[#4A5568]">
          {labels.fromCUN}
        </p>
        <p className="font-serif text-2xl text-[#1A1F24]">
          ${zone.suv}
          <span className="text-sm text-[#4A5568] font-sans ml-1">USD</span>
        </p>
      </div>
      <div className="sm:col-span-2 sm:text-right">
        <span className="text-xs uppercase tracking-[0.2em] text-[#2E4F4F] group-hover:text-[#C6A87C] transition-colors">
          {labels.seeQuote}
        </span>
      </div>
    </div>
  </button>
);

const QuoteModal = ({ hotel, zone, labels, onClose, onCheckout }) => {
  const [service, setService] = useState("one-way");
  const [vehicle, setVehicle] = useState("suv");

  const basePrice = zone[vehicle];
  const total = service === "round-trip" ? Math.round(basePrice * roundTripMultiplier) : basePrice;

  return (
    <div
      data-testid="quote-modal"
      className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#F9F6F0] w-full sm:max-w-lg sm:max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between p-6 border-b border-[#EAE6DF]">
          <div>
            <p className="text-[#2E4F4F] text-xs uppercase tracking-[0.3em] mb-2">
              {labels.quoteTitle}
            </p>
            <h3 className="font-serif text-2xl text-[#1A1F24] leading-tight">{hotel.name}</h3>
            <p className="text-xs text-[#4A5568] mt-1">{zone.label[labels.__lang]}</p>
          </div>
          <button
            data-testid="close-quote"
            onClick={onClose}
            className="p-2 text-[#4A5568] hover:text-[#1A1F24]"
            aria-label={labels.close}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Service */}
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#4A5568] mb-3">
              {labels.service}
            </p>
            <div className="grid grid-cols-2 gap-2">
              {["one-way", "round-trip"].map((opt) => (
                <button
                  key={opt}
                  data-testid={`quote-service-${opt}`}
                  onClick={() => setService(opt)}
                  className={`py-3 text-xs uppercase tracking-[0.2em] border transition-colors ${
                    service === opt
                      ? "bg-[#1A1F24] text-white border-[#1A1F24]"
                      : "bg-white text-[#1A1F24] border-[#1A1F24]/20 hover:border-[#1A1F24]"
                  }`}
                >
                  {opt === "one-way" ? labels.oneWay : labels.roundTrip}
                </button>
              ))}
            </div>
          </div>

          {/* Vehicle */}
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#4A5568] mb-3">
              {labels.vehicle}
            </p>
            <div className="space-y-2">
              {["suv", "van", "luxury"].map((v) => {
                const p = service === "round-trip"
                  ? Math.round(zone[v] * roundTripMultiplier)
                  : zone[v];
                return (
                  <button
                    key={v}
                    data-testid={`quote-vehicle-${v}`}
                    onClick={() => setVehicle(v)}
                    className={`w-full flex items-center justify-between p-4 border transition-colors ${
                      vehicle === v
                        ? "bg-white border-[#1A1F24]"
                        : "bg-white border-[#EAE6DF] hover:border-[#1A1F24]/60"
                    }`}
                  >
                    <span className="text-sm text-[#1A1F24]">{labels[v]}</span>
                    <span className="font-serif text-lg text-[#1A1F24]">${p}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Total */}
          <div className="bg-[#1A1F24] text-white p-5 flex items-baseline justify-between">
            <span className="text-xs uppercase tracking-[0.25em] text-[#C6A87C]">
              {labels.total}
            </span>
            <span className="font-serif text-3xl" data-testid="quote-total">
              ${total}
              <span className="text-sm text-white/60 font-sans ml-1">USD</span>
            </span>
          </div>

          <button
            data-testid="quote-checkout-btn"
            onClick={() => onCheckout({ service, vehicle, hotel, zone, total })}
            className="w-full bg-[#2E4F4F] hover:bg-[#1F3636] text-white py-4 text-xs uppercase tracking-[0.25em] transition-colors"
          >
            {labels.checkout}
          </button>
        </div>
      </div>
    </div>
  );
};

const HotelsPage = () => {
  const { lang } = useLang();
  const navigate = useNavigate();
  const labels = { ...hotelLabels[lang], __lang: lang };

  const [query, setQuery] = useState("");
  const [zoneFilter, setZoneFilter] = useState("all");
  const [selectedHotel, setSelectedHotel] = useState(null);

  const zones = useMemo(() => Object.entries(zonePricing).map(([id, z]) => ({ id, ...z })), []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return hotels.filter((h) => {
      if (zoneFilter !== "all" && h.zone !== zoneFilter) return false;
      if (!q) return true;
      const zoneLabel = (zonePricing[h.zone]?.label[lang] || "").toLowerCase();
      return h.name.toLowerCase().includes(q) || zoneLabel.includes(q);
    });
  }, [query, zoneFilter, lang]);

  const handleCheckout = ({ service, vehicle, hotel, zone, total }) => {
    // Persist so BookingForm can pre-fill.
    try {
      sessionStorage.setItem(
        "hrm_prefill",
        JSON.stringify({
          service_type: service,
          vehicle_type: vehicle,
          dropoff_location: `${hotel.name} — ${zone.label[lang]}`,
          estimated_price: total,
        })
      );
      window.dispatchEvent(new CustomEvent("hrm:prefill"));
    } catch (e) {
      // ignore
    }
    navigate("/#booking");
  };

  return (
    <div data-testid="hotels-page" className="bg-[#F9F6F0] min-h-screen">
      <Header />

      {/* Hero */}
      <section className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/45 to-[#F9F6F0]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12">
          <Link
            to="/"
            data-testid="hotels-back-home"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/80 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            {labels.backHome}
          </Link>
          <p className="text-[#C6A87C] text-xs uppercase tracking-[0.3em] mb-4">
            {labels.eyebrow}
          </p>
          <h1 className="font-serif text-4xl sm:text-6xl text-white tracking-tight max-w-3xl leading-[1.05]">
            {labels.title}
          </h1>
          <p className="mt-6 text-white/85 max-w-2xl leading-relaxed">{labels.subtitle}</p>
        </div>
      </section>

      {/* Search & filter */}
      <section className="border-b border-[#EAE6DF] bg-[#F9F6F0] sticky top-20 z-30 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-4 flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-[#4A5568] absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              data-testid="hotel-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={labels.searchPh}
              className="w-full h-12 pl-11 pr-4 bg-white border border-[#1A1F24]/20 text-sm focus:outline-none focus:border-[#1A1F24]"
            />
          </div>
          <select
            data-testid="zone-filter"
            value={zoneFilter}
            onChange={(e) => setZoneFilter(e.target.value)}
            className="h-12 px-4 bg-white border border-[#1A1F24]/20 text-sm focus:outline-none focus:border-[#1A1F24] min-w-[200px]"
          >
            <option value="all">{labels.filterAll}</option>
            {zones.map((z) => (
              <option key={z.id} value={z.id}>
                {z.label[lang]}
              </option>
            ))}
          </select>
        </div>
      </section>

      {/* Hotel list */}
      <main className="max-w-7xl mx-auto px-6 sm:px-12 py-12">
        {filtered.length === 0 && (
          <p data-testid="hotels-empty" className="text-center text-[#4A5568] py-16">
            {labels.empty}
          </p>
        )}
        <p className="text-xs uppercase tracking-[0.2em] text-[#4A5568] mb-6">
          {filtered.length} {lang === "es" ? "hoteles" : "hotels"}
        </p>
        <div className="grid grid-cols-1 gap-3">
          {filtered.map((h) => (
            <HotelRow
              key={h.id}
              hotel={h}
              zone={zonePricing[h.zone]}
              labels={labels}
              onSelect={setSelectedHotel}
            />
          ))}
        </div>
      </main>

      {selectedHotel && (
        <QuoteModal
          hotel={selectedHotel}
          zone={zonePricing[selectedHotel.zone]}
          labels={labels}
          onClose={() => setSelectedHotel(null)}
          onCheckout={handleCheckout}
        />
      )}

      <Footer />
    </div>
  );
};

export default HotelsPage;
